// Shared cache so multiple placements of a spotlight ad on the same page
// (e.g. bottom spotlight + sidebar card) all show the same building.
angular.module(NgAppName).factory('spotlightAdService', ['$q', '$timeout', '$window', 'crResourcesService', function($q, $timeout, $window, crResourcesService) {
	// CITY-5292 (owner 2026-09-16): the placements of a page show DIFFERENT ads. One entry per
	// (section, location, page, group) collects the placements registered in the same tick and sends
	// ONE /rpc/spotlight-ad request for as many distinct ads as there are placements; each placement
	// gets one ad. Placements are ranked large ("bottom" banner) > medium > sidebar, and the ranks are
	// compressed to the placements actually present: a page with medium + sidebar only (listing page,
	// popup) hands the 1st ad to the medium card and the 2nd to the sidebar. With fewer ads than
	// placements the lowest-ranked ones render nothing (never repeat an ad). A placement registering
	// later is appended and asks only for one more ad, excluding the ones already on the page.
	const MAX_SLOTS = 3;
	const PRIORITY_BY_SIZE = { large: 1, medium: 2, sidebar: 3 };
	const entries = {};

	// CITY-5476 QA: the sidebar slot is split 50/50 between the Featured Building card and a
	// Featured Listing, decided server-side per view — which makes it a reload lottery to look at.
	// `?spotlightAd=listing` / `?spotlightAd=building` on the PAGE url forces the half. It only
	// picks between two outcomes the slot already produces on its own (suppression, targeting and
	// the ad pool are all resolved before the split), so it cannot show anything a normal view
	// could not. Read once per page load and forwarded with every request of this page.
	const forcedAdHalf = (() => {
		const match = /[?&]spotlightAd=(listing|building)/.exec(String(($window.location || {}).search || ''));
		return match ? match[1] : null;
	})();

	const post = (payload) => crResourcesService.post2('/rpc/spotlight-ad', payload).$promise;

	const fetchMore = (entry, upTo) => {
		if (upTo <= entry.requested || entry.exhausted) return $q.when(entry);
		const payload = angular.extend({}, entry.payload, { count: upTo - entry.requested });
		const have = entry.ads.map((a) => a && a.ad && a.ad.id).filter((id) => id > 0);
		if (have.length) payload.excludeAdIds = have;
		const before = entry.requested;
		entry.requested = upTo;
		return post(payload).then((response) => {
			const list = (response && response.status && response.data) ? (response.data.ads || [response.data]) : [];
			list.forEach((ad) => { if (ad && ad.building) entry.ads.push(ad); });
			// CITY-5476: the Featured Listing half of the sidebar slot rides along with the
			// building ads. Kept on the entry (not the ad list) because it belongs to the
			// placement, not to a slot in the building rotation.
			if (response && response.data && response.data.listingAd) {
				entry.listingAd = response.data.listingAd;
				entry.listingAdWins = !!response.data.listingAdWins;
			}
			// fewer ads than asked: the pool is used up, later placements need no request
			if (list.length < payload.count) entry.exhausted = true;
			return entry;
		}, () => {
			// transport failure: let a later registration retry instead of starving these placements
			entry.requested = before;
			return entry;
		});
	};

	// the ad of a placement = the ad at its position in the page's rank order
	const slotView = (entry, priority) => {
		const index = entry.order.indexOf(priority);
		const ad = index >= 0 ? (entry.ads[index] || null) : null;
		// listingAd / listingAdWins (CITY-5476) are the same for every placement; only the
		// sidebar reads them, and only it knows whether its own slot came up empty.
		return { status: !!ad, data: ad, listingAd: entry.listingAd || null, listingAdWins: !!entry.listingAdWins };
	};

	return {
		slotFor: (size) => PRIORITY_BY_SIZE[String(size || 'large').toLowerCase()] || 1,

		// options.zoneId / zoneIds / hoodIds (location), options.buildingId (the page's building:
		// suppression + sale/rent side), options.pageId (ADPAGE_*), options.group (A/B) travel with
		// the request and are part of the entry key; options.slot is the placement's rank (slotFor).
		// options.refresh (CITY-5292 Jira 101488: a new search re-rolls the ads) is a per-refresh token
		// that is part of the key only, so every placement reacting to the same refresh shares ONE new
		// request instead of reusing the page-load payload.
		// Two placements of the SAME rank under one key (e.g. two medium cards) deliberately share one ad.
		// Resolves with { status, data } for THAT placement — the shape the placements always consumed.
		get: function(section, options) {
			if (!section) return null;
			const opts = options || {};
			const payload = { section: section };
			const zoneId = parseInt(opts.zoneId, 10);
			const buildingId = parseInt(opts.buildingId, 10);
			// CITY-5586: the page's listing (listing page / popup) — the Featured Listing tiers key off it.
			// Only the sidebar passes it; it is merged into the page's shared request below, NOT keyed.
			const listingId = parseInt(opts.listingId, 10);
			const pageId = parseInt(opts.pageId, 10);
			// CSV lists from data-zone-ids / data-hood-ids (directory regions, article geo target, searched hoods)
			const idList = (value) => String(value || '').split(',').map((v) => parseInt(v, 10)).filter((v) => v > 0);
			const zoneIds = idList(opts.zoneIds);
			const hoodIds = idList(opts.hoodIds);
			if (zoneId > 0) payload.zoneId = zoneId;
			if (zoneIds.length) payload.zoneIds = zoneIds;
			if (hoodIds.length) payload.hoodIds = hoodIds;
			if (buildingId > 0) payload.buildingId = buildingId;
			if (pageId > 0) payload.pageId = pageId;
			// tracking group of the page ('A' main page / 'B' other pages), recorded with every view / click
			const group = String(opts.group || '').trim().toUpperCase();
			if (group === 'A' || group === 'B') payload.group = group;
			const priority = Math.min(MAX_SLOTS, Math.max(1, parseInt(opts.slot, 10) || 1));

			const key = section + '|' + (payload.zoneId || 0) + '|' + zoneIds.join('.') + '|' + hoodIds.join('.') + '|' + (payload.buildingId || 0) + '|' + (payload.pageId || 0) + '|' + (payload.group || '') + '|' + String(opts.refresh || '');
			let entry = entries[key];
			if (!entry) {
				entry = entries[key] = { payload: payload, ads: [], order: [], requested: 0, exhausted: false, chain: null };
				// let every placement of this tick register before the first request goes out
				entry.chain = $timeout(angular.noop, 0).then(() => fetchMore(entry, entry.order.length));
			}
			// CITY-5476: only a sidebar placement can render the FL half, and building it costs
			// a listings lookup — ask for it only when this page actually has one. Set before the
			// deferred first request goes out, so a same-tick sidebar is always included.
			if (priority === PRIORITY_BY_SIZE.sidebar) {
				entry.payload.wantListingAd = true;
			}
			// CITY-5586: the page's listing travels the same way — merged into the ONE shared request.
			// Keying on it would split the popup's medium card (no listing id) and its sidebar into two
			// /rpc/spotlight-ad calls that could hand out the same building ad twice.
			if (listingId > 0) {
				entry.payload.listingId = listingId;
			}
			if (forcedAdHalf) {
				entry.payload.forceAdHalf = forcedAdHalf;
			}
			if (entry.order.indexOf(priority) < 0) {
				if (entry.requested === 0) {
					// before the first request: rank order (large, medium, sidebar) among the placements present
					entry.order.push(priority);
					entry.order.sort((a, b) => a - b);
				} else {
					// after it: appended, so the ads already handed out keep their placements
					entry.order.push(priority);
					entry.chain = entry.chain.then(() => fetchMore(entry, entry.order.length));
				}
			}
			return entry.chain.then(() => slotView(entry, priority));
		}
	};
}]);

// CITY-5292: per-placement tracking on the shared payload. RENTAL_DIRECTORY_IMG_TRACK.POSITION
// carries the size (ad.positions.large / medium / sidebar): the click link gets its `p=`
// rewritten and each placement fires its own /ad-server/view pixel once.
angular.module(NgAppName).factory('spotlightAdTracking', ['$window', function($window) {
	return {
		positionFor: function(data, size) {
			const positions = (data && data.ad && data.ad.positions) || {};
			return positions[size] || positions.large || 1;
		},
		// click link `p=` and the "Position: N" part of the GA data-track labels follow the placement
		linkWithPosition: function(link, position) {
			if (!link || !link.href) return link;
			const copy = angular.copy(link);
			copy.href = /([?&])p=[^&]*/.test(copy.href)
				? copy.href.replace(/([?&])p=[^&]*/, '$1p=' + position)
				: copy.href + (copy.href.indexOf('?') === -1 ? '?' : '&') + 'p=' + position;
			angular.forEach(copy.track || {}, (attrs) => {
				angular.forEach(attrs || {}, (value, name) => {
					if (typeof value === 'string' && /Position: \d+/.test(value)) {
						attrs[name] = value.replace(/Position: \d+/, 'Position: ' + position);
					}
				});
			});
			return copy;
		},
		fireView: function(data, position) {
			const viewUrl = data && data.ad && data.ad.viewUrl;
			if (!viewUrl) return;
			const img = new $window.Image();
			img.src = viewUrl + '&p=' + position;
		},
		// the advertiser's own tracking script (link.external) is appended once per ad payload
		// (a placement that receives the same ad object again does not append it twice)
		appendExternalOnce: function(data, container) {
			if (!data || !data.link || !data.link.external || data._externalAppended || !container) return;
			data._externalAppended = true;
			const div = document.createElement('div');
			div.style.display = 'none';
			div.innerHTML = data.link.external;
			container.appendChild(div);
		}
	};
}]);

angular.module(NgAppName).component('crSpotlightAd', {
	templateUrl: 'crSpotlightAd.html',
	bindings: {
		section: '@',
		// CITY-5292: 'large' (default) or 'medium' — the same card design on the same
		// payload; medium only scales it down (`_medium` class) and tracks POSITION 2
		size: '@',
		zoneId: '@',      // hood zone targeting (AD_SPONSORED_REGION), single zone (zone landing)
		zoneIds: '@',     // CSV of zones the page is about (directory regions, market data, luxury condos, new-dev zone)
		hoodIds: '@',     // CSV of hoods the page is about (article geo target, searched neighborhoods) → their zones
		buildingId: '@',  // the page's building: zone targeting + advertiser-page suppression
		pageId: '@',      // ADPAGE_* of the rendering page (tracking PAGE_ID)
		group: '@'        // tracking AD_GROUPING of the page: 'A' main page, 'B' other pages (the controller's choice)
	},
	controller: ['$scope', '$element', '$window', '$timeout', '$interval', '$sce', 'spotlightAdService', 'spotlightAdTracking',
		function($scope, $element, $window, $timeout, $interval, $sce, spotlightAdService, spotlightAdTracking) {

		this.loaded = false;
		this.isMedium = false;
		this.link = null;
		this.building = null;
		this.prices = [];
		this.gallery = [];
		// CMS "Description / Headline" and "Text Blurb" are separate fields; either or both may be set
		this.headline = '';
		this.blurb = '';
		this.hasHeadline = false;
		this.hasBlurb = false;
		this.activeSlide = 0;
		this.stripOffset = 0;

		// CITY-5292: paid variant (Featured Building Ad 77, paid + in term).
		// The image strip auto-advances (spec: cycle 5-7 images) and the primary
		// link.href targets the advertiser site while sponsoring (the CTA itself
		// reads "Learn More" in both states — Jira 101423). No auto-advance under
		// prefers-reduced-motion; paused on hover, on keyboard focus inside the
		// banner and while the banner is off-screen / in a hidden tab, plus the
		// visible pause / play control WCAG 2.2.2 asks for (Jira 101423 asked for
		// that control to be removed — kept deliberately, flagged back to QA).
		this.ad = {};
		this.sponsoring = false;
		this.cyclePaused = false;
		this.focusPaused = false;
		this.offscreen = false;
		this.userPaused = false;   // visible pause / play control (WCAG 2.2.2)
		this.cycling = false;      // true once the auto-advance timer is running
		var cycleTimer = null;
		var visibilityObserver = null;
		var CYCLE_INTERVAL_MS = 5000;
		var REDUCED_MOTION = !!($window.matchMedia && $window.matchMedia('(prefers-reduced-motion: reduce)').matches);

		var onFocusIn = () => {
			this.focusPaused = true;
		};
		var onFocusOut = () => {
			this.focusPaused = false;
		};

		this.pauseCycle = () => {
			this.cyclePaused = true;
		};
		this.resumeCycle = () => {
			this.cyclePaused = false;
		};
		this.togglePause = () => {
			this.userPaused = !this.userPaused;
		};
		this.isCyclePaused = () => this.userPaused || this.cyclePaused || this.focusPaused || this.offscreen;

		var startCycle = () => {
			if (REDUCED_MOTION || cycleTimer) {
				return;
			}
			this.cycling = true;
			cycleTimer = $interval(() => {
				if (!this.isCyclePaused()) {
					this.nextSlide();
				}
			}, CYCLE_INTERVAL_MS);

			$element[0].addEventListener('focusin', onFocusIn);
			$element[0].addEventListener('focusout', onFocusOut);
			if ('IntersectionObserver' in $window) {
				visibilityObserver = new $window.IntersectionObserver((entries) => {
					this.offscreen = !entries.some((entry) => entry.isIntersecting);
				});
				visibilityObserver.observe($element[0]);
			}
		};

		var stopCycle = () => {
			if (cycleTimer) {
				$interval.cancel(cycleTimer);
				cycleTimer = null;
				$element[0].removeEventListener('focusin', onFocusIn);
				$element[0].removeEventListener('focusout', onFocusOut);
			}
			if (visibilityObserver) {
				visibilityObserver.disconnect();
				visibilityObserver = null;
			}
			this.cycling = false;
		};

		// CITY-5292 (`_bleed`, article page): a `_wide` band rendered inside a fixed content column stretches
		// itself to the real viewport width — margin-left = -(own left offset), width = documentElement
		// clientWidth (scrollbar excluded). Pure CSS (`calc(50% - 50vw)`) sat off-center: 100vw counts the
		// always-on scrollbar and the column need not be centered. Re-measured on resize.
		var isBleed = $element[0].classList.contains('_bleed');
		var applyBleed = () => {
			if (!isBleed) return;
			const el = $element[0];
			el.style.marginLeft = '';
			el.style.width = '';
			const viewport = $window.document.documentElement.clientWidth;
			if (!viewport) return;
			const left = el.getBoundingClientRect().left;
			el.style.boxSizing = 'border-box';
			el.style.marginLeft = (-left) + 'px';
			el.style.width = viewport + 'px';
		};
		var resizeTimer = null;
		var onResize = () => {
			if (resizeTimer) $timeout.cancel(resizeTimer);
			resizeTimer = $timeout(applyBleed, 100, false);
		};
		if (isBleed) {
			$window.addEventListener('resize', onResize);
		}

		this.$onDestroy = () => {
			stopCycle();
			if (isBleed) {
				$window.removeEventListener('resize', onResize);
				if (resizeTimer) $timeout.cancel(resizeTimer);
			}
		};

		this.$onInit = () => {
			this.isMedium = String(this.size || 'large').toLowerCase() === 'medium';
		};

		// CITY-5292 (Jira 101488): the search results page re-rolls its Featured Building Ad after a new
		// search (filter / sort / page). crToggleView (listings) / crInquisitio (building directory)
		// broadcasts the searched location once the results are in; the card reloads with it (an empty
		// list = no targeting on that axis, the whole pool) and a per-refresh token so the shared service
		// issues a NEW request instead of replaying the page-load payload.
		// The directory filters by zone (regions) as well as by hood, so both axes travel with the event:
		// whichever key the sender omits keeps its page-load value, and the two must not disagree.
		$scope.$on('spotlightAd:refresh', (e, data) => {
			if (!this.section) return;
			if (data && data.hoodIds !== undefined) {
				this.hoodIds = String(data.hoodIds || '');
			}
			if (data && data.zoneIds !== undefined) {
				this.zoneIds = String(data.zoneIds || '');
			}
			stopCycle();
			this.loaded = false;
			this.building = null;
			this.prices = [];
			this.gallery = [];
			this.activeSlide = 0;
			this.stripOffset = 0;
			this.ad = {};
			this.sponsoring = false;
			this.userPaused = false;
			load((data && data.token) || Date.now());
		});

		this.$postLink = () => {
			load(null);
		};

		var load = (refreshToken) => {
			// slot 1 (large) or 2 (medium): which of the page's distinct ads this placement shows
			const slot = spotlightAdService.slotFor(this.isMedium ? 'medium' : 'large');
			const promise = spotlightAdService.get(this.section, { zoneId: this.zoneId, zoneIds: this.zoneIds, hoodIds: this.hoodIds, buildingId: this.buildingId, pageId: this.pageId, group: this.group, slot: slot, refresh: refreshToken });
			if (!promise) return;

			promise.then((response) => {
				if (response && response.data && response.data.building) {
					let d = response.data;
					const position = spotlightAdTracking.positionFor(d, this.isMedium ? 'medium' : 'large');
					this.link = spotlightAdTracking.linkWithPosition(d.link, position);
					spotlightAdTracking.fireView(d, position);
					this.building = d.building;
					this.prices = d.prices || [];
					this.gallery = d.gallery || [];
					const headline = String(d.building.headline || '').trim();
					const blurb = String(d.building.blurb || '').trim();
					this.hasHeadline = headline !== '';
					this.hasBlurb = blurb !== '';
					this.headline = $sce.trustAsHtml(headline);
					this.blurb = $sce.trustAsHtml(blurb);

					this.ad = d.ad || {};
					this.sponsoring = !!this.ad.sponsoring;
					if (this.sponsoring && this.gallery.length > 1) {
						startCycle();
					}

					// Fire GA analytics view event
					if (d.link && d.link.analytics) {
						$timeout(() => {
							let div = document.createElement('div');
							div.innerHTML = d.link.analytics;
							let scripts = div.getElementsByTagName('script');
							for (let i = 0; i < scripts.length; i++) {
								try { eval(scripts[i].text); } catch(e) {}
							}
						});
					}

					// Append the advertiser's external tracking pixel (once per shared payload)
					$timeout(() => {
						spotlightAdTracking.appendExternalOnce(d, $element[0].querySelector('.spotlight-root'));
					});

					this.loaded = true;
					if (isBleed) {
						$timeout(applyBleed, 0, false);
					}

					// Recompute strip offset once Angular has rendered the slides
					// and after each image finishes loading (images have natural
					// dimensions only after `load` fires; offsetWidth before that
					// is 0 for narrow vertical images).
					$timeout(() => {
						this.recalcStripOffset();
						const imgs = $element[0].querySelectorAll('.spotlight-slide img');
						imgs.forEach((img) => {
							if (img.complete && img.naturalWidth) return;
							img.addEventListener('load', () => {
								$timeout(() => this.recalcStripOffset());
							}, { once: true });
						});
					});
				}
			});
		};

		this.recalcStripOffset = () => {
			const slides = $element[0].querySelectorAll('.spotlight-slide');
			const slider = $element[0].querySelector('.spotlight-slider');
			const strip = $element[0].querySelector('.spotlight-strip');
			let offset = 0;
			for (let i = 0; i < this.activeSlide && i < slides.length; i++) {
				offset += slides[i].offsetWidth;
			}
			// Clamp so the strip's right edge never goes past the container's
			// right edge — prevents the empty black gutter at the last slide.
			// When clamped, earlier slides peek from the left of the viewport
			// instead of empty space appearing on the right.
			if (strip && slider) {
				const maxOffset = Math.max(0, strip.scrollWidth - slider.clientWidth);
				if (offset > maxOffset) offset = maxOffset;
			}
			this.stripOffset = -offset;
		};

		this.nextSlide = () => {
			if (this.gallery.length < 2) return;
			this.activeSlide = (this.activeSlide + 1) % this.gallery.length;
			this.recalcStripOffset();
		};

		this.prevSlide = () => {
			if (this.gallery.length < 2) return;
			this.activeSlide = (this.activeSlide - 1 + this.gallery.length) % this.gallery.length;
			this.recalcStripOffset();
		};
	}]
});
